import { styled } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';
import { SharedBasketExpiry } from '@/lib/types';
import {
  ExpiryOptions,
  resolveExpiryDate,
} from './ExpirySelectHelpers';

const ExpirySelect = ({
  value,
  onChange,
  expiresAt,
  disabled,
}: {
  value: SharedBasketExpiry;
  onChange: (value: SharedBasketExpiry) => void;
  // Once the basket exists, the server's expires_at wins over the preview.
  expiresAt?: string;
  disabled?: boolean;
}) => {
  const resolved = resolveExpiryDate(value, expiresAt);

  return (
    <>
      <FieldLabel id="expiry-label">
        <FormattedMessage
          id="linkExpires"
          defaultMessage="Link expires"
        />
      </FieldLabel>
      <Segmented role="radiogroup" aria-labelledby="expiry-label">
        {ExpiryOptions.map((option) => (
          <Option key={option.value} $selected={option.value === value}>
            <HiddenRadio
              type="radio"
              name="shared-basket-expiry"
              value={option.value}
              checked={option.value === value}
              disabled={disabled}
              onChange={() => onChange(option.value)}
            />
            {option.label}
          </Option>
        ))}
      </Segmented>
      <Hint>
        <FormattedMessage
          id="linkExpiresHint"
          defaultMessage="Stops working on {date}. The files stay in the portal — only the link expires."
          values={{ date: <b>{resolved}</b> }}
        />
      </Hint>
    </>
  );
};

const FieldLabel = styled('div')(({ theme }) => ({
  fontSize: 13,
  fontWeight: 600,
  color: theme.palette.grey600,
  marginTop: theme.spacing(2.5),
  marginBottom: theme.spacing(1),
}));

const Segmented = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
}));

const Option = styled('label', {
  shouldForwardProp: (prop) => prop !== '$selected',
})<{ $selected: boolean }>(({ theme, $selected }) => ({
  flex: 1,
  textAlign: 'center',
  padding: theme.spacing(1.25, 1),
  borderRadius: theme.radii.sm,
  border: `1px solid ${
    $selected ? theme.palette.primary.main : theme.palette.grey300
  }`,
  background: $selected ? theme.palette.red50 : theme.palette.white,
  color: $selected
    ? theme.palette.primary.main
    : theme.palette.grey600,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  '&:focus-within': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

// Visually hidden, but a real radio - keyboard and screen-reader semantics
// come for free.
const HiddenRadio = styled('input')({
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
});

const Hint = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(1),
  fontSize: 13,
  lineHeight: '18px',
  color: theme.palette.grey500,
}));

export default ExpirySelect;
